import React, { useState } from 'react';
import { submitContactForm } from '../services/api';
import { FaEnvelope, FaClock } from 'react-icons/fa';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        sourceTenant: '',
        targetTenant: '',
        numberOfUsers: '',  // Add this
        message: '',
        // Honeypot: rendered off-screen, never filled by a human. The API
        // discards any submission where it is set. Not named "website" -
        // browsers autofill url-ish fields and a false positive silently
        // bins a real enquiry.
        subjectRef: ''
    });
    const [status, setStatus] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        try {
            await submitContactForm(formData);
            setStatus('success');
            setFormData({
                name: '',
                email: '',
                company: '',
                sourceTenant: '',
                targetTenant: '',
                numberOfUsers: '',
                message: '',
                subjectRef: ''
            });
        } catch (error) {
            setStatus('error');
        }
    };

    return (
        <>
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-primary-50 to-white py-12">
        <div className="container-width">
        <h1 className="text-4xl md:text-5xl font-light text-primary-900 mb-4">
        Get In <span className="font-normal text-red-900">Touch</span>
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl">
        Start your migration journey with a free consultation. Our experts are ready to help.
        </p>
        </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-12 md:py-16">
        <div className="container-width">
        <div className="grid md:grid-cols-5 gap-12">
        {/* Contact Form */}
        <div className="md:col-span-3">
        <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-light text-primary-900 mb-6">
        Please fill out the form <span className="font-normal">below</span>
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Honeypot - humans never see or fill this; the API discards any
        submission where it is set. Positioned off-screen rather than
        display:none, which some bots skip, and out of both tab order and
        the accessibility tree. */}
        <div style={{ position: 'absolute', left: '-9999px', top: 'auto' }} aria-hidden="true">
        <label htmlFor="subjectRef">Reference</label>
        <input
        type="text"
        id="subjectRef"
        name="subjectRef"
        tabIndex={-1}
        autoComplete="off"
        value={formData.subjectRef}
        onChange={handleChange}
        />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
        <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
        Name *
        </label>
        <input
        type="text"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
        placeholder="John Smith"
        />
        </div>
        <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
        Email *
        </label>
        <input
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        required
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
        placeholder="john@company.com"
        />
        </div>
        </div>

        <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
        Company *
        </label>
        <input
        type="text"
        name="company"
        value={formData.company}
        onChange={handleChange}
        Required
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
        placeholder="Your Company Name"
        />
        </div>

        {/* Source Tenant field with arrow */}
        <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
        Source Tenant *
        </label>
        <div className="relative">
        <select
        name="sourceTenant"
        value={formData.sourceTenant}
        onChange={handleChange}
        required
        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition appearance-none pr-10 bg-gray-50/50"
        >
        <option value="">Select your current platform</option>
        <option value="Google Cloud">Google Cloud</option>
        <option value="Microsoft 365">Microsoft 365</option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
        </svg>
        </div>
        </div>
        </div>

        {/* Target Tenant field with arrow */}
        <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
        Target Tenant *
        </label>
        <div className="relative">
        <select
        name="targetTenant"
        value={formData.targetTenant}
        onChange={handleChange}
        required
        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition appearance-none pr-10 bg-gray-50/50"
        >
        <option value="">Select your target platform</option>
        <option value="Google Cloud">Google Cloud</option>
        <option value="Microsoft 365">Microsoft 365</option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
        </svg>
        </div>
        </div>
        </div>

        {/* Number of Users field with arrow */}
        <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
        Number of users to be migrated *
        </label>
        <div className="relative">
        <select
        name="numberOfUsers"
        value={formData.numberOfUsers}
        onChange={handleChange}
        required
        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition appearance-none pr-10 bg-gray-50/50"
        >
        <option value="">Select number of users</option>
        <option value="1-100">1 - 100</option>
        <option value="100-250">100 - 250</option>
        <option value="250-500">250 - 500</option>
        <option value="500-1000">500 - 1000</option>
        <option value="1000+">1000+</option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
        </svg>
        </div>
        </div>
        </div>

        <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
        Additional Information
        </label>
        <textarea
        name="message"
        value={formData.message}
        onChange={handleChange}
        rows="5"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition resize-none"
        placeholder="Tell us about your migration needs..."
        ></textarea>
        </div>

        <div>
        <button
        type="submit"
        className="btn-primary w-full md:w-auto px-8"
        disabled={status === 'sending'}
        >
        {status === 'sending' ? 'Sending...' : 'Send Message'}
        </button>

        {status === 'success' && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            Thank you! We'll be in touch within 24 hours.
            </div>
        )}
        {status === 'error' && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Something went wrong. Please try again or email us directly.
            </div>
        )}
        </div>
        </form>
        </div>
        </div>

        {/* Contact Information */}
        <div className="md:col-span-2">
        <div className="space-y-6">

        {/* Direct Contact Card - More subtle */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-6 border border-primary-200">
        <h3 className="text-xl font-medium text-primary-900 mb-4">Direct Contact</h3>
        <div className="space-y-3">
        <div className="flex items-start gap-3">
        <FaEnvelope className="mt-1 flex-shrink-0 text-primary-600" />
        <div>
        <p className="font-medium text-gray-700">Email</p>
        <a href="mailto:info@themigrationexperts.com" className="text-primary-600 hover:text-primary-700">
        info@themigrationexperts.com
        </a>
        </div>
        </div>
        </div>
        </div>

        {/* What Happens Next Card - Matching subtle style */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-6 border border-primary-200">
        <h3 className="text-xl font-medium text-primary-900 mb-4">What Happens Next?</h3>
        <ul className="space-y-3">
        <li className="flex items-start gap-2">
        <span className="text-primary-600 font-bold">1.</span>
        <span className="text-gray-700">We'll review your requirements</span>
        </li>
        <li className="flex items-start gap-2">
        <span className="text-primary-600 font-bold">2.</span>
        <span className="text-gray-700">Schedule a consultation call</span>
        </li>
        <li className="flex items-start gap-2">
        <span className="text-primary-600 font-bold">3.</span>
        <span className="text-gray-700">Provide a detailed migration plan</span>
        </li>
        <li className="flex items-start gap-2">
        <span className="text-primary-600 font-bold">4.</span>
        <span className="text-gray-700">Begin your seamless migration</span>
        </li>
        </ul>
        </div>

        {/* Quick Response - Matching subtle style */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-6 border border-primary-200">
        <div className="flex items-center gap-3">
        <FaClock className="text-2xl text-primary-600" />
        <div>
        <p className="font-medium text-primary-900">Quick Response</p>
        <p className="text-sm text-gray-600">Usually within 2-4 hours</p>
        </div>
        </div>
        </div>
        </div>
        </div>
        </div>
        </div>
        </section>
        </>
    );
};

export default Contact;
